export const state = () => ({
  participantQuestionnaires: [],
  filteredQuestionnaires: [],
  activeQuestionnaire: {},
  questionnaireInProgress: {
    _id: null,
    name: '',
    projectId: null,
    propertyId: null,
    body: '',
    subject: '',
    targetedUser: null,
    questions: []
  },
  questionnaireOptions: {
    filter: 'all', // 'all', 'sent', 'received'
    search: '',
    searchField: 'all',
    sortBy: 'created',
    sortOrder: 'desc'
  }
})

export const getters = {
  getParticipantQuestionnaires: state => state.participantQuestionnaires,
  getFilteredQuestionnaires: state => state.filteredQuestionnaires,
  getActiveQuestionnaire: state => state.activeQuestionnaire,
  getQuestionnaireInProgress: state => state.questionnaireInProgress,
  getQuestionnaireOptions: state => state.questionnaireOptions,
  getQuestionnaireSearchTerm: state => state.questionnaireOptions.search,
  getQuestionnaireSortBy: state => state.questionnaireOptions.sortBy,
  getQuestionnaireListFilter: state => state.questionnaireOptions.filter,
  // Names of the loaded questionnaires that include a given question
  getQuestionnaireNamesByQuestionId: state => (questionId) => {
    if (!questionId) {
      return []
    }
    return state.participantQuestionnaires
      .filter(q => q && Array.isArray(q.questions) && q.questions.some(qq => String(qq?._id || qq) === String(questionId)))
      .map(q => q.name)
  }
}

export const actions = {
  // targetedUsers = [{ _id, email, firstName, lastName, company, address, city, state, zip, phone }]
  // - new users will not have an _id, but other fields ARE required
  // - existing user _id is required only
  // - inviteSubject, inviteMessage are not required
  // - questions ARE required
  async addQuestionnaire({ dispatch }, { targetedUsers, participantId, projectId, propertyId, name, inviteSubject, inviteMessage, showProjectDescription, attachments, questions }) {
    try {
      const { questionnaire } = await this.$axios.$post(`/questionnaire/${participantId}/${projectId}/${propertyId}`,
        { targetedUsers, name, inviteSubject, inviteMessage, showProjectDescription, attachments, questions })
      return questionnaire
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/addQuestionnaire', info: err },
        { root: true })
    }
  },

  async deleteQuestionnaire({ commit, dispatch }, { participantId, projectId, propertyId, questionnaireId }) {
    try {
      const { result } = await this.$axios.$delete(`/questionnaire/${participantId}/${projectId}/${propertyId}/${questionnaireId}`)
      // We need to remove the questionnaire from the participantQuestionnaire list
      if (result === 'ok') {
        commit('removeParticipantQuestionnaire', questionnaireId)
        return true
      }
      return false
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/deleteQuestionnaire', info: err },
        { root: true })
    }
  },

  async fetchQuestionnaireById({ commit, dispatch }, { questionnaireId }) {
    try {
      const { questionnaire } = await this.$axios.$get(`/questionnaire/${questionnaireId}`)
      if (!Array.isArray(questionnaire.answers)) {
        questionnaire.answers = []
      }
      commit('setActiveQuestionnaire', questionnaire)
      commit('answers/setAnswers', questionnaire.answers, { root: true })
      return questionnaire
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/fetchQuestionnaireById', info: err },
        { root: true })
    }
  },

  async fetchQuestionnairesForParticipant({ commit, dispatch }, { participantId, projectId, propertyId }) {
    try {
      const { questionnaires } = await this.$axios.$get(`/questionnaires/${participantId}/${projectId}/${propertyId}`)
      commit('setParticipantQuestionnaires', questionnaires)
      return questionnaires
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/fetchQuestionnairesForParticipant', info: err },
        { root: true })
    }
  },

  async fetchAllQuestionnairesForParticipant({ commit, dispatch }) {
    try {
      const { questionnaires } = await this.$axios.$get('/questionnaires')
      commit('setParticipantQuestionnaires', questionnaires)
      dispatch('filterQuestionnaires')
      return questionnaires
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/fetchAllQuestionnairesForParticipant', info: err },
        { root: true })
    }
  },

  async sendQuestionnaire({ dispatch }, { participantId, projectId, propertyId, questionnaireId }) {
    try {
      const { result } = await this.$axios.$put(
        `/questionnaire/send/${participantId}/${projectId}/${propertyId}`,
        { questionnaireId })
      return result === 'ok'
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/sendQuestionnaire', info: err },
        { root: true })
    }
  },

  async resendQuestionnaire({ dispatch }, { participantId, projectId, propertyId, questionnaireId }) {
    try {
      const { result } = await this.$axios.$put(
        `/questionnaire/resend/${participantId}/${projectId}/${propertyId}`,
        { questionnaireId })
      return result === 'ok'
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/resendQuestionnaire', info: err },
        { root: true })
    }
  },

  // LSO-859: properties in the project this questionnaire can be copied to
  async fetchQuestionnaireCopyTargets({ dispatch }, { questionnaireId }) {
    try {
      const { targets } = await this.$axios.$get(`/questionnaire/copy/targets/${questionnaireId}`)
      return targets || []
    } catch (err) {
      dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/fetchQuestionnaireCopyTargets', info: err },
        { root: true })
      return []
    }
  },

  // LSO-859: copy a questionnaire to other properties in the same project. Each
  // copy is invited as it is created, so there is no draft step to send later.
  // Returns { created, skipped } so the caller can report partial copies.
  // LSO-857: copyResponses opts the copies in to the responses already on file
  // for each target property; they start blank without it.
  async copyQuestionnaire({ dispatch }, { questionnaireId, propertyIds, name, targetedUsers, copyResponses }) {
    try {
      const { result, created, skipped } = await this.$axios.$post(
        `/questionnaire/copy/${questionnaireId}`,
        { propertyIds, name, targetedUsers, copyResponses })
      if (result === 'ok') {
        return { created: created || [], skipped: skipped || [] }
      }
      return null
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/copyQuestionnaire', info: err },
        { root: true })
    }
  },

  // LSO-857: reopen a finished questionnaire so its recipients can answer or
  // update their answers (notifies the recipients by email)
  async revertQuestionnaireToWorking({ commit, dispatch }, { questionnaireId }) {
    try {
      const { result, questionnaire } = await this.$axios.$put(`/questionnaire/revert/${questionnaireId}`)
      if (result === 'ok') {
        commit('updateQuestionnaire', questionnaire)
        return questionnaire
      }
      return null
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/revertQuestionnaireToWorking', info: err },
        { root: true })
    }
  },

  async updateQuestionnaire({ commit, dispatch }, { participantId, projectId, propertyId, questionnaireId, targetedUsers, inviteSubject, inviteMessage, showProjectDescription, attachments, questions, answers, status, name }) {
    try {
      const { result, questionnaire } = await this.$axios.$put(`/questionnaire/${participantId}/${projectId}/${propertyId}/${questionnaireId}`,
        { targetedUsers, inviteSubject, inviteMessage, showProjectDescription, attachments, questions, answers, status, name })
      if (result === 'ok') {
        commit('updateQuestionnaire', questionnaire)
      }
      return questionnaire
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/updateQuestionnaire', info: err },
        { root: true })
    }
  },

  async updateQuestionnaireStatusAsCollaborator({ commit, dispatch }, { questionnaireId, status }) {
    try {
      const { result, questionnaire } = await this.$axios.$put(`/questionnaire/status/${questionnaireId}`,
        { status })
      if (result === 'ok') {
        commit('updateQuestionnaire', questionnaire)
      }
      return questionnaire
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/updateQuestionnaire', info: err },
        { root: true })
    }
  },

  async verifyQuestionnaire({ dispatch }, { questionnaireId }) {
    try {
      const { result } = await this.$axios.$get(`/questionnaire/verify/${questionnaireId}`)
      return result === 'ok'
    } catch (err) {
      return dispatch(
        'global/setNetworkError',
        { method: 'questionnaires/verifyQuestionnaire', info: err },
        { root: true })
    }
  },

  setActiveQuestionnaire({ commit }, questionnaire) {
    commit('setActiveQuestionnaire', questionnaire)
  },

  setQuestionnaireInProgress({ commit }, { _id, name, projectId, propertyId, body, subject, teaser, targetedUser, showProjectDescription, attachments, questions }) {
    commit('setQuestionnaireInProgress', { _id, name, projectId, propertyId, body, subject, teaser, targetedUser, showProjectDescription, attachments, questions })
  },

  setQuestionnaireInProgressName({ commit }, name) {
    commit('setQuestionnaireInProgressName', name)
  },

  setQuestionnaireInProgressBody({ commit }, body) {
    commit('setQuestionnaireInProgressBody', body)
  },

  setQuestionnaireInProgressSubject({ commit }, subject) {
    commit('setQuestionnaireInProgressSubject', subject)
  },

  setQuestionnaireInProgressQuestions({ commit }, questions = []) {
    commit('setQuestionnaireInProgressQuestions', questions)
  },

  setQuestionnaireInProgressRecipient({ commit }, targetedUser = {}) {
    commit('setQuestionnaireInProgressRecipient', targetedUser)
  },

  setQuestionnaireInProgressAttachments({ commit }, attachments = []) {
    commit('setQuestionnaireInProgressAttachments', attachments)
  },

  setQuestionnaireInProgressShowProjectDescription({ commit }, showProjectDescription) {
    commit('setQuestionnaireInProgressShowProjectDescription', showProjectDescription)
  },

  // Filtering and searching actions
  filterQuestionnaires({ state, commit, rootGetters }) {
    let filtered = [...state.participantQuestionnaires]
    const currentUserId = rootGetters['user/user']?._id

    // Apply type filter (sent vs received)
    if (state.questionnaireOptions.filter && state.questionnaireOptions.filter !== 'all') {
      filtered = filtered.filter((questionnaire) => {
        const recipientIds = (questionnaire.targetedUsers || []).map(u => String(u?._id || u))
        const isRecipient = recipientIds.includes(String(currentUserId))

        if (state.questionnaireOptions.filter === 'sent') {
          // Questionnaires sent by current user (user is NOT a recipient)
          return !isRecipient
        } else if (state.questionnaireOptions.filter === 'received') {
          // Questionnaires received by current user (user IS a recipient)
          return isRecipient
        }
        return true
      })
    }

    // Apply search filter
    if (state.questionnaireOptions.search) {
      const searchTerm = state.questionnaireOptions.search.toLowerCase()
      filtered = filtered.filter((questionnaire) => {
        switch (state.questionnaireOptions.searchField) {
          case 'project':
            return questionnaire.project?.name?.toLowerCase().includes(searchTerm) || false
          case 'property':
            return questionnaire.property?.propertyDetail?.name?.toLowerCase().includes(searchTerm) || false
          case 'requestor': {
            // Requestor is the person who sent/created the questionnaire
            const createdByMatch = questionnaire.createdBy?.fullName?.toLowerCase().includes(searchTerm) || false
            const participantMatch = questionnaire.participant?.fullName?.toLowerCase().includes(searchTerm) || false
            return createdByMatch || participantMatch
          }
          case 'requestee': {
            // Requestee is the person who receives the questionnaire (a recipient)
            const recipients = questionnaire.targetedUsers || []
            const targetedUserMatch = recipients.some(u => u?.fullName?.toLowerCase().includes(searchTerm))
            const targetedUserEmailMatch = recipients.some(u => u?.email?.toLowerCase().includes(searchTerm))
            return targetedUserMatch || targetedUserEmailMatch
          }
          case 'questionnaire':
            return questionnaire.name?.toLowerCase().includes(searchTerm) || false
          case 'status':
            return questionnaire.status && typeof questionnaire.status === 'string'
              ? questionnaire.status.toLowerCase().includes(searchTerm)
              : false
          case 'all': {
            // Search across all fields
            const questionnaireMatch = questionnaire.name?.toLowerCase().includes(searchTerm) || false
            const projectMatch = questionnaire.project?.name?.toLowerCase().includes(searchTerm) || false
            const propertyMatch = questionnaire.property?.propertyDetail?.name?.toLowerCase().includes(searchTerm) || false

            // Requestor (sender) matches
            const createdByMatch = questionnaire.createdBy?.fullName?.toLowerCase().includes(searchTerm) || false
            const participantMatch = questionnaire.participant?.fullName?.toLowerCase().includes(searchTerm) || false

            // Requestee (recipient) matches
            const recipients = questionnaire.targetedUsers || []
            const targetedUserMatch = recipients.some(u => u?.fullName?.toLowerCase().includes(searchTerm))
            const targetedUserEmailMatch = recipients.some(u => u?.email?.toLowerCase().includes(searchTerm))

            const statusMatch = questionnaire.status && typeof questionnaire.status === 'string'
              ? questionnaire.status.toLowerCase().includes(searchTerm)
              : false

            return questionnaireMatch || projectMatch || propertyMatch ||
              createdByMatch || participantMatch || targetedUserMatch ||
              targetedUserEmailMatch || statusMatch
          }
          default:
            return questionnaire.project?.name?.toLowerCase().includes(searchTerm) || false
        }
      })
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const { sortBy, sortOrder } = state.questionnaireOptions
      let aVal, bVal

      switch (sortBy) {
        case 'questionnaire':
          aVal = a.name || ''
          bVal = b.name || ''
          break
        case 'project':
          aVal = a.project?.name || ''
          bVal = b.project?.name || ''
          break
        case 'property':
          aVal = a.property?.propertyDetail?.name || ''
          bVal = b.property?.propertyDetail?.name || ''
          break
        case 'requestor':
          aVal = a.targetedUsers?.[0]?.fullName || ''
          bVal = b.targetedUsers?.[0]?.fullName || ''
          break
        case 'created':
          aVal = new Date(a.created || 0)
          bVal = new Date(b.created || 0)
          break
        default:
          aVal = new Date(a.created || 0)
          bVal = new Date(b.created || 0)
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    commit('setFilteredQuestionnaires', filtered)
  },

  setQuestionnaireSearch({ commit, dispatch }, searchTerm) {
    commit('setQuestionnaireSearch', searchTerm)
    dispatch('filterQuestionnaires')
  },

  setQuestionnaireSearchField({ commit, dispatch }, searchField) {
    commit('setQuestionnaireSearchField', searchField)
    dispatch('filterQuestionnaires')
  },

  setQuestionnaireSortBy({ commit, dispatch }, sortBy) {
    commit('setQuestionnaireSortBy', sortBy)
    dispatch('filterQuestionnaires')
  },

  setQuestionnaireSortOrder({ commit, dispatch }, sortOrder) {
    commit('setQuestionnaireSortOrder', sortOrder)
    dispatch('filterQuestionnaires')
  },

  setQuestionnaireListFilter({ commit, dispatch }, filter) {
    commit('setQuestionnaireListFilter', filter)
    dispatch('filterQuestionnaires')
  }
}

export const mutations = {
  init(state) {
    state.activeQuestionnaire = {}
    state.questionnaireInProgress = {
      _id: null,
      name: '',
      projectId: null,
      propertyId: null,
      body: '',
      subject: '',
      targetedUser: null,
      questions: []
    }
  },

  setParticipantQuestionnaires(state, questionnaires) {
    state.participantQuestionnaires = questionnaires
  },

  // this will be used when accessing Questionnaire Detail page
  setActiveQuestionnaire(state, questionnaire) {
    state.activeQuestionnaire = questionnaire
  },

  // this is used in the questionnaire wizard name field
  setQuestionnaireInProgressName(state, name) {
    state.questionnaireInProgress.name = name
  },

  // this is used in the questionnaire wizard body field
  setQuestionnaireInProgressBody(state, body) {
    state.questionnaireInProgress.body = body
  },

  // this is used in the questionnaire wizard subject field
  setQuestionnaireInProgressSubject(state, subject) {
    state.questionnaireInProgress.subject = subject
  },

  // this is used in the questionnaire wizard questions field
  setQuestionnaireInProgressQuestions(state, questions = []) {
    questions.forEach((question) => {
      const questionInStore = state.questionnaireInProgress.questions.find(questionInProgress => questionInProgress._id === question._id)
      if (questionInStore) {
        // question exists, delete from store
        const newQuestions = state.questionnaireInProgress.questions.filter(questionInProgress => questionInProgress._id !== questionInStore._id)
        state.questionnaireInProgress.questions = newQuestions
      } else {
        // question doesn't exist, add it
        state.questionnaireInProgress.questions.push(question)
      }
    })
  },

  // this is used in the questionnaire wizard recipient field
  setQuestionnaireInProgressRecipient(state, targetedUser) {
    state.questionnaireInProgress.targetedUser = targetedUser
  },

  setQuestionnaireInProgress(state, { _id = null, name = '', projectId = null, propertyId = null, body = '', subject = '', targetedUser = null, showProjectDescription = false, attachments = [], questions = '' }) {
    state.questionnaireInProgress = {
      _id,
      name,
      projectId,
      propertyId,
      body,
      subject,
      targetedUser,
      showProjectDescription,
      attachments,
      questions
    }
  },

  setQuestionnaireInProgressAttachments(state, attachments) {
    state.questionInProgress.attachments = attachments
  },

  setQuestionnaireInProgressShowProjectDescription(state, showProjectDescription) {
    state.questionInProgress.showProjectDescription = showProjectDescription
  },

  removeParticipantQuestionnaire(state, questionnaireId) {
    const idx = state.participantQuestionnaires.findIndex(pq => String(pq._id) === String(questionnaireId))
    state.participantQuestionnaires.splice(idx, 1)
  },

  updateQuestionnaire(state, questionnaire) {
    const idx = state.participantQuestionnaires.findIndex(pq => String(pq._id) === String(questionnaire._id))
    state.participantQuestionnaires[idx] = questionnaire
  },

  // Filtering and search mutations
  setFilteredQuestionnaires(state, questionnaires) {
    state.filteredQuestionnaires = questionnaires
  },

  setQuestionnaireSearch(state, search) {
    state.questionnaireOptions.search = search
  },

  setQuestionnaireSearchField(state, searchField) {
    state.questionnaireOptions.searchField = searchField
  },

  setQuestionnaireSortBy(state, sortBy) {
    state.questionnaireOptions.sortBy = sortBy
  },

  setQuestionnaireSortOrder(state, sortOrder) {
    state.questionnaireOptions.sortOrder = sortOrder
  },

  setQuestionnaireListFilter(state, filter) {
    state.questionnaireOptions.filter = filter
  }
}

// TODO: these are api calls Derrick setup that will need to be hooked up and tested

// GET /questionnaire/:questionnaireId => retrieves individual questionnaire, will need to add security here
// GET /questionnaires/:projectId/:propertyId => retrieves a list of questionnaires associated with a property.

// POST /c/:projectId/:propertyId => adds new questionnaire and sends invite

// PUT /questionnaire/:projectId/:propertyId => edit/update questionnaire

// PUT /questionnaire/:projectId/:propertyId/resend => resends/updates invite and sends

// DELETE /questionnaire/:projectId/:propertyId/:questionnaireId => deletes the questionnaire
